import { useEffect, useState, useCallback, useRef, DependencyList } from 'react';

/**
 * Generic hook for listening to Chrome runtime messages
 * Note: For type-safe message handling, use the messaging client returned by createMessageHandlers().build()
 */
export function useScribeMessage(
  handler: (
    message: any,
    sender: chrome.runtime.MessageSender,
    sendResponse: (response: any) => void,
  ) => void | boolean,
  deps: DependencyList = [],
) {
  const handlerRef = useRef(handler);

  // Keep handler reference up to date
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (
      message: any,
      sender: chrome.runtime.MessageSender,
      sendResponse: (response: any) => void,
    ) => {
      return handlerRef.current(message, sender, sendResponse);
    };

    chrome.runtime.onMessage.addListener(listener);

    return () => {
      chrome.runtime.onMessage.removeListener(listener);
    };
  }, deps);
}

/**
 * Hook for listening to messages with filtering
 */
export function useScribeMessageFilter(
  filter: (message: any, sender: chrome.runtime.MessageSender) => boolean,
  handler: (message: any, sender: chrome.runtime.MessageSender) => any,
  deps: DependencyList = [],
) {
  const filterRef = useRef(filter);
  const handlerRef = useRef(handler);

  // Keep refs up to date
  useEffect(() => {
    filterRef.current = filter;
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (
      message: any,
      sender: chrome.runtime.MessageSender,
      sendResponse: (response: any) => void,
    ) => {
      if (filterRef.current(message, sender)) {
        try {
          const result = handlerRef.current(message, sender);

          if (result instanceof Promise) {
            result
              .then((response) => sendResponse(response))
              .catch((error) => {
                console.error('Error in filtered message handler:', error);
                sendResponse({ error: error.message });
              });
            return true;
          } else if (result !== undefined) {
            sendResponse(result);
            return false;
          }
        } catch (error) {
          console.error('Error in filtered message handler:', error);
          sendResponse({ error: error instanceof Error ? error.message : String(error) });
          return false;
        }
      }
      return false;
    };

    chrome.runtime.onMessage.addListener(listener);

    return () => {
      chrome.runtime.onMessage.removeListener(listener);
    };
  }, deps);
}

/**
 * Note: The type-safe hooks (useTypedScribeMessage, useScribeQuery, useScribeMutation)
 * have been removed in favor of using the messaging client directly.
 *
 * Instead, use the messaging client returned by createMessageHandlers():
 *
 * const messaging = createMessageHandlers()...build();
 *
 * // In your component:
 * const [data, setData] = useState();
 *
 * useEffect(() => {
 *   messaging.send('getMessage').then(setData);
 * }, []);
 */
